import { useState } from 'react';
import { LinkIcon, PlusIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import { laufwerkConnectorTrennen, laufwerkKopplungscode, type LaufwerkConnector, type LaufwerkStatus } from '../../api/client';
import { useDialog } from '../../contexts/ConfirmContext';
import { scanDauer, verstummt, vorZeit, zahl } from '../../utils/laufwerk';

/** Karte „Verbindung": welche Connectors melden sich, Connector trennen, Kopplungscode für einen neuen erzeugen. */

export function ConnectorZeile({ c, aktionen }: { c: Pick<LaufwerkConnector, 'name' | 'zuletzt' | 'zustand' | 'widerrufen'>; aktionen?: React.ReactNode }) {
  const still = verstummt(c.zuletzt);
  const z = c.zustand ?? {};
  const fehler = typeof z.fehler === 'number' ? z.fehler : 0;
  return (
    <li className="rounded-lg border border-ps-dark-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words font-medium text-ps-dark-900">{c.name}</span>
            {c.widerrufen && <span className="badge badge-neutral">getrennt</span>}
          </div>
          {!c.widerrufen && (
            <div className={`mt-0.5 flex items-center gap-1.5 text-xs ${still ? 'font-medium text-rose-700' : 'text-ps-dark-500'}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${still ? 'bg-rose-500' : 'bg-emerald-500'}`} />
              {c.zuletzt ? <>Zuletzt gemeldet {vorZeit(c.zuletzt)}</> : <>Hat sich noch nie gemeldet</>}
              {c.zuletzt && still && <span> — keine Meldung seit über 10 Minuten</span>}
            </div>
          )}
        </div>
        {aktionen}
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Dateien</dt><dd className="numerical font-medium text-ps-dark-800">{z.dateien !== undefined ? zahl(z.dateien) : '—'}</dd></div>
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Wartend</dt><dd className="numerical font-medium text-ps-dark-800">{z.wartend !== undefined ? zahl(z.wartend) : '—'}</dd></div>
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Scan-Dauer</dt><dd className="numerical font-medium text-ps-dark-800">{scanDauer(z.scan_sekunden)}</dd></div>
      </dl>
      {(fehler > 0 || z.version) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ps-dark-400">
          {fehler > 0 && <span className="text-amber-800">{zahl(fehler)} {fehler === 1 ? 'Datei' : 'Dateien'} beim Hochladen fehlgeschlagen</span>}
          {z.version && <span>Version {z.version}</span>}
        </div>
      )}
    </li>
  );
}

export default function VerbindungKarte({ status, flash, onChanged }: {
  status: LaufwerkStatus | null;
  flash: (msg: string, variant?: 'ok' | 'err') => void;
  onChanged: () => void;
}) {
  const { confirm } = useDialog();
  const [code, setCode] = useState<{ code: string; gueltig_minuten: number } | null>(null);
  const [busy, setBusy] = useState(false);
  // Quellen (Cloud, WebDAV …) stehen in der eigenen Karte — hier nur die Connectors im Haus
  const connectors = (status?.connectors ?? []).filter((c) => !c.widerrufen && c.art !== 'quelle');

  const kopplungscode = async () => {
    setBusy(true);
    try { setCode(await laufwerkKopplungscode()); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
    finally { setBusy(false); }
  };

  const trennen = async (c: LaufwerkConnector) => {
    if (!await confirm({
      title: 'Connector trennen?',
      message: `«${c.name}» wird getrennt und meldet sich nicht mehr. Bereits durchsuchbare Dateien bleiben im Index. Zum erneuten Verbinden brauchen Sie einen neuen Kopplungscode.`,
      okLabel: 'Trennen', danger: true,
    })) return;
    try { await laufwerkConnectorTrennen(c.id); flash(`«${c.name}» getrennt.`); onChanged(); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="page-h1 flex items-center gap-2"><ServerStackIcon className="h-5 w-5 text-ps-dark-400" /> Connector im Haus</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
            Der Connector läuft bei Ihnen im Haus, liest das Laufwerk und meldet sich regelmässig. Ihre Dateien bleiben dabei, wo sie sind.
          </p>
        </div>
        <button onClick={() => void kopplungscode()} disabled={busy} className="btn-primary flex shrink-0 items-center gap-1.5 text-sm">
          <PlusIcon className="h-4 w-4" /> Kopplungscode erzeugen
        </button>
      </div>

      {code && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-primary-800"><LinkIcon className="h-4 w-4" /> Kopplungscode</div>
          <div className="numerical mt-1 break-all font-mono text-3xl font-semibold tracking-widest text-ps-dark-900 sm:text-4xl">{code.code}</div>
          <p className="mt-2 text-xs text-ps-dark-600">{code.gueltig_minuten} Minuten gültig, einmal verwendbar. Geben Sie ihn beim Einrichten des Connectors ein.</p>
          <div className="mt-2 flex justify-center gap-2">
            <button type="button" className="btn-ghost text-xs" onClick={() => { void navigator.clipboard?.writeText(code.code).then(() => flash('Code kopiert'), () => flash('Kopieren nicht möglich', 'err')); }}>Kopieren</button>
            <button type="button" className="btn-ghost text-xs" onClick={() => setCode(null)}>Ausblenden</button>
          </div>
        </div>
      )}

      {!status ? (
        <div className="space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : connectors.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ps-dark-200 px-4 py-6 text-center text-sm text-ps-dark-500">
          Noch kein Connector verbunden. Ihn braucht es für Laufwerke im Firmennetz (NAS, Windows-Server): Kopplungscode erzeugen und beim Einrichten eingeben. Cloud-Speicher und Server im Internet binden Sie oben unter „Quellen“ an.
        </div>
      ) : (
        <ul className="space-y-3">
          {connectors.map((c) => (
            <ConnectorZeile key={c.id} c={c} aktionen={
              <button onClick={() => void trennen(c)} className="shrink-0 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50">
                Connector trennen
              </button>
            } />
          ))}
        </ul>
      )}
    </div>
  );
}
